import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, getYear } from "date-fns";
import { TriangleAlert } from "lucide-react";

import { FormField } from "components/form/FormField/FormField";
import { Accordion } from "components/ui/Accordion";
import { Button } from "components/ui/Button";
import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
import { DatePickerField } from "components/ui/DatePickerField";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { Select } from "components/ui/Select/Select";
import { getReviewTypeTitle,REVIEW_TYPES } from "features/cdm/constants/reviewTypes";
import { applyYearPrefill } from "features/cdm/utils/applyYearPrefill";
import { getEligibleCdmDiagnosisIds } from "features/cdm/utils/cdmEligibility";
import {
  findContractDoctor,
  getMissingContractDoctorFields,
} from "features/cdm/utils/cdmGating";
import { useEthnicGroups } from "features/patients/hooks/useEthnicGroups";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { usePatientHealthIdentifiers } from "features/patients/hooks/usePatientHealthIdentifiers";
import { resolveEthnicityCode } from "features/patients/utils";
import { useStaffMembers } from "features/practice/hooks/useStaffMembers";
import { onInvalidSubmit } from "utils/reportFormErrors";
import { formatEircode } from "utils/validators";

import { diagnosisItems } from "../../constants/diagnosisItems";
import { otherMajorDiagnosisItems } from "../../constants/otherMajorDiagnosisItems";
import style from "./AddCDMReviewDialog.module.css";
import { formSchema } from "./schema";

export const AddCDMReviewDialog = ({
  isAddCdmReviewDialogOpen,
  setIsAddCdmReviewDialogOpen,
  addNewCDMItem,
  doctor,
  reviewType = REVIEW_TYPES.CDM_REGISTRATION,
  prefillDiagnosisCodes = [],
  prefillDiagnosisYears = {},
  interimAnnualPrefillCodes = [],
  interimAnnualPrefillYears = {},
}) => {
  const { data: staffMembers } = useStaffMembers();
  const { data: patient } = usePatientData();
  const { data: healthIdentifiers } = usePatientHealthIdentifiers();
  const { data: ethnicGroups } = useEthnicGroups();

  const [showReasonError, setShowReasonError] = useState(false);

  const missingPatientFields = [
    !patient?.ethnicityCode && "Ethnic Group",
    !patient?.address?.country && "Country",
    !patient?.financialClass && "Patient Classification",
  ].filter(Boolean);

  const currentYear = new Date().getFullYear().toString();

  // Private patients may only register under the Diabetes programme — restrict
  // the selectable conditions accordingly.
  const eligibleDiagnosisIds = getEligibleCdmDiagnosisIds(
    patient?.financialClass
  );
  const eligibleDiagnosisItems = diagnosisItems.filter((item) =>
    eligibleDiagnosisIds.includes(item.id)
  );

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      consultationDate: format(new Date(), "yyyy-MM-dd"),
      eircode: "",
      contractDoctor: doctor?.staffMember?.id?.toString(),
      ethnicityCode: "",
      diabetesType2: false,
      asthma: false,
      copd: false,
      heartFailure: false,
      atrialFibrillation: false,
      ischaemicHeartDisease: false,
      stroke: false,
      tia: false,
      diabetesType2Year: currentYear,
      asthmaYear: currentYear,
      copdYear: currentYear,
      heartFailureYear: currentYear,
      atrialFibrillationYear: currentYear,
      ischaemicHeartDiseaseYear: currentYear,
      strokeYear: currentYear,
      tiaYear: currentYear,
      diabetesType2AttendingHospital: false,
      asthmaAttendingHospital: false,
      copdAttendingHospital: false,
      heartFailureAttendingHospital: false,
      atrialFibrillationAttendingHospital: false,
      ischaemicHeartDiseaseAttendingHospital: false,
      strokeAttendingHospital: false,
      tiaAttendingHospital: false,
      myocardialInfarction: false,
      dementia: false,
      cancerStage2Plus: false,
      seriousMobilityIssues: false,
      chronicKidneyDisease: false,
      significantMentalIllness: false,
      hypertensionOnTreatment: false,
      inflammatoryArthritis: false,
      otherMajorDiagnosisOtherText: "",
    },
  });

  useEffect(() => {
    form.setValue("contractDoctor", doctor?.staffMember?.id?.toString() ?? "");
  }, [doctor, form]);

  useEffect(() => {
    if (isAddCdmReviewDialogOpen && patient?.address?.postCode) {
      form.setValue(
        "eircode",
        formatEircode(patient.address.postCode) || patient.address.postCode
      );
    }
    if (isAddCdmReviewDialogOpen && patient?.ethnicityCode && ethnicGroups?.length) {
      // Normalise the patient's ethnicity (code or description) to the canonical
      // code so the Select — which matches by `g.code` — prefills correctly.
      const code = resolveEthnicityCode(patient.ethnicityCode, ethnicGroups);
      if (code) form.setValue("ethnicityCode", code);
    }
  }, [
    isAddCdmReviewDialogOpen,
    patient?.address?.postCode,
    patient?.ethnicityCode,
    ethnicGroups,
    form,
  ]);

  // Prefill the diagnosis checkboxes from a completed OCF/PP outcome on a fresh
  // CDM registration (KIN-1162). Mapped by code id; user can still change them.
  useEffect(() => {
    if (!isAddCdmReviewDialogOpen) return;
    if (reviewType !== REVIEW_TYPES.CDM_REGISTRATION) return;
    if (!prefillDiagnosisCodes?.length) return;

    eligibleDiagnosisItems.forEach((item) => {
      if (prefillDiagnosisCodes.includes(item.id)) {
        form.setValue(item.field, true);
      }
    });
    applyYearPrefill({
      form,
      items: eligibleDiagnosisItems,
      selectedIds: prefillDiagnosisCodes,
      yearById: prefillDiagnosisYears,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddCdmReviewDialogOpen, reviewType, prefillDiagnosisCodes?.join(",")]);

  // Carry the diagnoses from the patient's most recent CDM review into a new
  // interim/annual review — a subsequent review covers the already-registered
  // diseases (CDM spec, "Adding a new diagnosis after first registration").
  // Iterating over eligibleDiagnosisItems applies the intersection of
  // previously-selected and currently-visible conditions, so a diagnosis no
  // longer offered (e.g. after a card-type change to Private) is not pre-ticked.
  useEffect(() => {
    if (!isAddCdmReviewDialogOpen) return;
    if (
      reviewType !== REVIEW_TYPES.CDM_INTERIM &&
      reviewType !== REVIEW_TYPES.CDM_ANNUAL
    )
      return;
    if (!interimAnnualPrefillCodes?.length) return;

    eligibleDiagnosisItems.forEach((item) => {
      if (interimAnnualPrefillCodes.includes(item.id)) {
        form.setValue(item.field, true);
      }
    });
    applyYearPrefill({
      form,
      items: eligibleDiagnosisItems,
      selectedIds: interimAnnualPrefillCodes,
      yearById: interimAnnualPrefillYears,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddCdmReviewDialogOpen, reviewType, interimAnnualPrefillCodes?.join(",")]);

  const handleEircodeBlur = (e) => {
    const val = e.target.value;
    if (val) {
      form.setValue("eircode", formatEircode(val), { shouldValidate: true });
    }
  };

  const handleFormSubmit = (data) => {
    const contractDoctor = staffMembers?.find(
      (item) => item.id === Number(data.contractDoctor)
    );

    const reviewDate =
      data.consultationDate || format(new Date(), "yyyy-MM-dd");

    // Only eligible conditions can be submitted — a Private patient must not
    // register a non-Diabetes condition even if a stale/prefilled form value is
    // still set for a checkbox that is no longer rendered (KIN-1228).
    const checkedItems = eligibleDiagnosisItems.filter(
      (item) => data[item.field]
    );

    if (checkedItems.length === 0) {
      setShowReasonError(true);

      return;
    }

    const checkedIds = checkedItems.map((item) => item.id);
    const checkedNames = checkedItems.map((item) => item.name);

    const yearOfDiagnosis = checkedItems.reduce((acc, item) => {
      acc[item.id] = Number(data[`${item.field}Year`]);
      return acc;
    }, {});

    const attendingHospital = checkedItems.reduce((acc, item) => {
      acc[item.id] = data[`${item.field}AttendingHospital`] ?? false;
      return acc;
    }, {});

    const otherMajorDiagnoses = {
      diagnoses: otherMajorDiagnosisItems
        .filter((item) => data[item.field])
        .map((item) => item.id),
      otherText: data.otherMajorDiagnosisOtherText?.trim() || "",
    };

    addNewCDMItem({
      id: crypto.randomUUID(),
      date: new Date(reviewDate),
      reviewDate,
      name: getReviewTypeTitle(reviewType),
      reviewType,
      status: "Incomplete",
      type: "cdm",
      registeredDate: new Date(),
      reviewingDate: new Date(),
      reviewingDoctor:
        contractDoctor?.firstName + " " + contractDoctor?.lastName,
      createdBy:
        doctor?.staffMember?.firstName + " " + doctor?.staffMember?.lastName,
      reasonForReview: checkedNames.join(", "),
      indications: checkedNames,
      registrationReasons: checkedIds,
      medications: [],
      yearOfDiagnosis: yearOfDiagnosis,
      attendingHospital: attendingHospital,
      otherMajorDiagnoses,
      eircode: data.eircode ? data.eircode.replace(/\s+/g, "") : data.eircode,
      contractDoctor: data.contractDoctor,
      ethnicityCode: data.ethnicityCode,
      diabetesType2: data.diabetesType2,
      asthma: data.asthma,
      copd: data.copd,
      heartFailure: data.heartFailure,
      atrialFibrillation: data.atrialFibrillation,
      ischaemicHeartDisease: data.ischaemicHeartDisease,
      stroke: data.stroke,
      tia: data.tia,

      diabetesType2Year: data.diabetesType2Year,
      asthmaYear: data.asthmaYear,
      copdYear: data.copdYear,
      heartFailureYear: data.heartFailureYear,
      atrialFibrillationYear: data.atrialFibrillationYear,
      ischaemicHeartDiseaseYear: data.ischaemicHeartDiseaseYear,
      strokeYear: data.strokeYear,
      tiaYear: data.tiaYear,
    });
    setIsAddCdmReviewDialogOpen(false);
  };

  /*
   * The review is claimed under the contract doctor's own GMS contract number, so a doctor whose
   * staff record has none cannot submit — and used to find that out only at the end, from a
   * backend error naming neither the field nor where to fix it (KIN-1936). Watched rather than
   * read once: the doctor is a select, and changing it changes whether the review can proceed.
   */
  const selectedContractDoctor = findContractDoctor(
    staffMembers,
    form.watch("contractDoctor")
  );
  const missingContractDoctorFields = getMissingContractDoctorFields(
    selectedContractDoctor
  );
  const isBlockedFromSubmitting =
    missingPatientFields.length > 0 || missingContractDoctorFields.length > 0;

  const importedData = {
    gmsNumber:
      healthIdentifiers?.medicalCardNo || patient?.medicalCardNumber || "",
    contractDoctor:
      doctor?.staffMember?.firstName + " " + doctor?.staffMember?.lastName,
    patientDOB: patient?.dateOfBirth,
  };

  const isAnyDiagnosisSelected = eligibleDiagnosisItems.some((item) =>
    form.watch(item.field)
  );

  return (
    <Dialog
      open={isAddCdmReviewDialogOpen}
      title="Add Chronic Disease Management Review"
      onClose={() => setIsAddCdmReviewDialogOpen(false)}
      width={1100}
    >
      <div style={{ overflowY: "auto", maxHeight: "calc(95vh - 140px)" }}>
        {missingPatientFields.length > 0 && (
          <div style={{ backgroundColor: "#fff3cd", border: "1px solid #ffc107", borderRadius: "12px", padding: "16px", marginBottom: "16px", color: "#856404", fontSize: "14px", fontWeight: "600" }}>
            ⚠️ CDM cannot be submitted — patient is missing {missingPatientFields.join(" and ")}. Please update the patient profile first.
          </div>
        )}
        {/* Names the doctor, the field and where it is set. The point of blocking here rather than
            at submit is that all three are knowable before the first question is answered. */}
        {missingContractDoctorFields.length > 0 && (
          <div style={{ backgroundColor: "#fff3cd", border: "1px solid #ffc107", borderRadius: "12px", padding: "16px", marginBottom: "16px", color: "#856404", fontSize: "14px", fontWeight: "600" }}>
            ⚠️ CDM cannot be submitted — {selectedContractDoctor?.firstName}{" "}
            {selectedContractDoctor?.lastName} has no{" "}
            {missingContractDoctorFields.join(" and ")}. The review is claimed under it, so it
            must be set on their staff record (Settings → Users) before starting.
          </div>
        )}
        <div className={style.importedDataWrapper}>
          <div className={style.importedDataItem}>
            <p className={style.importedDataItemLabel}>GMS Number</p>
            <p className={style.importedDataItemValue}>
              {importedData.gmsNumber}
            </p>
          </div>
          <div className={style.importedDataItem}>
            <p className={style.importedDataItemLabel}>Contract Doctor</p>
            <p className={style.importedDataItemValue}>
              {importedData.contractDoctor}
            </p>
          </div>
          <div className={style.importedDataItem}>
            <p className={style.importedDataItemLabel}>Patient DOB</p>
            <p className={style.importedDataItemValue}>
              {importedData.patientDOB}
            </p>
          </div>
        </div>
        <FormProvider {...form}>
          <form
            className={style.addReferralForm}
            onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("AddCDMReviewDialog"))}
          >
            <div className={style.inputGroupGrid4Columns}>
              <FormField name="consultationDate">
                <label className={style.pregnancyFormLabel}>
                  Consultation date
                </label>
                <DatePickerField
                  value={form.watch("consultationDate")}
                  onChange={(value) =>
                    form.setValue("consultationDate", value, {
                      shouldValidate: true,
                    })
                  }
                  maxDate={new Date()}
                />
              </FormField>
              <FormField name="contractDoctor">
                <label className={style.addReferralFormLabel}>
                  Contract Doctor
                </label>
                <Select
                  options={staffMembers?.map((staff) => ({
                    value: staff.id,
                    label: `${staff.firstName} ${
                      staff.lastName
                    }${
                      staff.staffRole
                        ? ` (${staff.staffRole.toLowerCase()})`
                        : ""
                    }${staff.active ? "" : " (inactive)"}`,
                  }))}
                  value={form.watch("contractDoctor") ?? ""}
                  onChange={(value) =>
                    form.setValue("contractDoctor", value, {
                      shouldValidate: true,
                    })
                  }
                  required
                />
              </FormField>
              <FormField name="ethnicityCode">
                <label className={style.addReferralFormLabel}>Ethnicity</label>
                <Select
                  options={(ethnicGroups ?? []).map((group) => group.description)}
                  value={ethnicGroups?.find((g) => g.code === form.watch("ethnicityCode"))?.description ?? ""}
                  onChange={(value) => {
                    const selectedGroup = ethnicGroups?.find((g) => g.description === value);
                    form.setValue("ethnicityCode", selectedGroup?.code ?? "", { shouldValidate: true });
                  }}
                  required
                />
              </FormField>
              <FormField name="eircode">
                <label className={style.pregnancyFormLabel}>Eircode</label>
                <input
                  type="text"
                  className="form-control"
                  {...form.register("eircode")}
                  onBlur={handleEircodeBlur}
                />
              </FormField>
            </div>
            <div className={style.indicatorsSection}>
              <h2 style={{ fontSize: "18px", fontWeight: "bold" }}>
                Diagnosis
              </h2>
              {showReasonError && !isAnyDiagnosisSelected && (
                <div className={style.reasonRequiredMessage} role="status">
                  <TriangleAlert
                    size={20}
                    className={style.reasonRequiredIcon}
                  />
                  <p className={style.reasonRequiredText}>
                    Select at least one diagnosis to start the review.
                  </p>
                </div>
              )}
              <div className={style.tableContainer}>
                <table className={style.table}>
                  <colgroup>
                    <col style={{ width: "30px" }} />
                    <col />
                    <col style={{ width: "180px" }} />
                    <col style={{ width: "180px" }} />
                  </colgroup>
                  <thead className={style.thead}>
                    <tr>
                      <th className={style.th} />
                      <th className={style.th}>Reason</th>
                      <th className={style.th}>Attending Hospital</th>
                      <th className={style.th}>Year</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eligibleDiagnosisItems.map((item) => (
                      <tr key={item.id}>
                        <td className={style.td}>
                          <CustomCheckbox
                            name={item.field}
                            checked={form.watch(item.field)}
                            onChange={(e) => form.setValue(item.field, e.target.checked)}
                          />
                        </td>
                        <td className={style.td}>{item.name}</td>
                        <td className={style.td}>
                          <CustomCheckbox
                            name={`${item.field}AttendingHospital`}
                            checked={form.watch(`${item.field}AttendingHospital`) ?? false}
                            disabled={!form.watch(item.field)}
                            onChange={(e) =>
                              form.setValue(`${item.field}AttendingHospital`, e.target.checked)
                            }
                          />
                        </td>
                        <td className={style.td}>
                          <Select
                            small
                            options={Array.from(
                              { length: 150 },
                              (_, i) => getYear(new Date()) - i
                            ).map((year) => ({ value: year, label: year }))}
                            disabled={!form.watch(item.field)}
                            value={form.watch(`${item.field}Year`) ?? ""}
                            onChange={(value) => form.setValue(`${item.field}Year`, value)}
                            required
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className={`${style.indicatorsSection} ${style.noDivider}`}>
              <Accordion
                items={[
                  {
                    id: "otherMajorDiagnoses",
                    title: "Other Major Diagnoses",
                    content: (
                      <>
                        <div className={style.diagnosisList}>
                          {otherMajorDiagnosisItems.map((item) => (
                            <label
                              key={item.id}
                              className={style.diagnosisListItem}
                            >
                              <CustomCheckbox
                                name={item.field}
                                checked={form.watch(item.field)}
                                onChange={(e) =>
                                  form.setValue(item.field, e.target.checked)
                                }
                              />
                              <span>{item.name}</span>
                            </label>
                          ))}
                        </div>
                        <FormField name="otherMajorDiagnosisOtherText">
                          <label
                            className={`${style.addReferralFormLabel} ${style.otherFreeTextLabel}`}
                          >
                            Other (free text)
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            {...form.register("otherMajorDiagnosisOtherText")}
                          />
                        </FormField>
                      </>
                    ),
                  },
                ]}
                allowMultiple={false}
                className={style.plainAccordion}
              />
            </div>

            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setIsAddCdmReviewDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isBlockedFromSubmitting}>
                Confirm
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </div>
    </Dialog>
  );
};
